/**
 * Content Section Control - V2
 * 
 * CONTROL GROUP: Original tab-based layout (no sticky header tabs).
 * This is an exact copy of the original ContentSection for A/B testing.
 * 
 * @see content-section-with-tabs.tsx for the variation with sticky tabs
 */

import { useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { InView } from 'react-intersection-observer';
import { Box } from '@mui/material';
import { getTenantById, isTenantExpCA, isTenantExpInService, isZoocasaTenant, TenantId } from '@tenants';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_CLICK_ADDRESS_PAGE_COMMUTE_TAB,
  GTM_CLICK_ADDRESS_PAGE_DEMOGRAPHICS_TAB,
  GTM_CLICK_ADDRESS_PAGE_DESCRIPTION_TAB,
  GTM_CLICK_ADDRESS_PAGE_MAP_TAB,
  GTM_CLICK_ADDRESS_PAGE_ROOMS_TAB,
  GTM_CLICK_ADDRESS_PAGE_SALES_ACTIVITY_TAB,
  GTM_CLICK_ADDRESS_PAGE_SCHOOL_NAME,
  GTM_VIEW_ADDRESS_PAGE_AVERAGE_SOLD_PRICE_TAB,
  GTM_VIEW_ADDRESS_PAGE_KEY_FACTS_TAB,
  GTM_VIEW_ADDRESS_PAGE_PRICE_HISTORY_TAB,
  GTM_VIEW_ADDRESS_PAGE_ZOOPRAISAL,
} from 'constants/events';
import { listingComponentHeights } from 'constants/listing-components';
import { listingPageModalIds } from 'constants/test-constants';
import { useListingRestrictions } from 'hooks/use-listing-restrictions';
import { useIsNotMobile } from 'hooks/use-size-class';
import { ComplianceRequirement } from 'types/compliance';
import { CountryCodeList } from 'types/countries';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { getPropertySubTypeLabelKey, getPropertyTypeLabelKey } from 'types/reso';
import Tabs from 'components/tabs';
import Tab from 'components/tabs/tab';
import Map, { TrackEventLocation } from 'components/map/listing';
import LoadingText from 'components/loading-text';

import type { AddressPageCommonData, AddressPageEventHandlers } from '../../../shared';
import {
  Description,
  KeyFacts,
  PropertyDetails,
  Rooms,
  resolveDisplayRooms,
} from '../../../components';
import { ListingDetails } from '../../../components/key-facts';
import PriceHistory from '../../../components/price-history';
import LocationUnavailable from '../../../components/location-unavailable';
import { FEATURE_FLAGS, hasFeatureFlag } from '../../../types';
import { MortgageCtaBanner } from 'sections/listing/content-sections';
import styles from './style.module.scss';

//#region Dynamic Imports
const ProviderDisclaimer = dynamic(() => import('../../../components/provider-disclaimer'), {
  loading: () => <LoadingText />,
});

const PropertyAppraisal = dynamic(() => import('../../../components/property-appraisal'), {
  loading: () => <LoadingText />,
});

const ChartGroupLoading = dynamic(() => import('components/charts/chart-helpers/chart-group-loading'), {
  loading: () => <LoadingText />,
});

const NearbySchools = dynamic(() => import('../../../components/nearby-schools'), {
  loading: () => <LoadingText />,
});

const NeighbourhoodDemographics = dynamic(() => import('../../../components/neighbourhood-demographics'), {
  loading: () => <LoadingText />,
});

const TravelTime = dynamic(() => import('../../../components/travel-time'), {
  loading: () => <LoadingText />,
});

const MyListingAgentSection = dynamic(
  () => import('sections/listing/my-listing-agent').then(m => m.MyListingAgentSection),
  { loading: () => <LoadingText />, ssr: false },
);
//#endregion

//#region Constants
const TAB_IDS = {
  PROPERTY_DETAILS: 'property-details',
  KEY_FACTS: 'key-facts',
  DESCRIPTION: 'description',
  ROOMS: 'rooms',
  MAP: 'map',
  AVG_SOLD_PRICE: 'avg-sold-price',
  SALES_ACTIVITY: 'sales-activity',
  SCHOOLS: 'schools',
  DEMOGRAPHICS: 'demographics',
  COMMUTE: 'commute',
} as const;
//#endregion

//#region Sub Components
function AppraisalTitle({ tenantId }: { tenantId: TenantId }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const context = isZoocasaTenant(tenantId) ? 'zoopraisal' : undefined;
  return (
    <h2>{t(LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_TITLE, { defaultValue: 'Home Estimate Zoopraisal®', context })}</h2>
  );
}
//#endregion

export interface ContentSectionControlProps {
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
  className?: string;
  priceHistoryRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Content Section Control
 * 
 * CONTROL GROUP: Original tab-based layout.
 * Renders tabs (key facts, description, rooms, map),
 * price history, charts, and secondary info.
 */
export function ContentSectionControl({ 
  data, 
  handlers, 
  className,
  priceHistoryRef: externalPriceHistoryRef,
}: ContentSectionControlProps) {
  const {
    tenantId,
    address,
    listing,
    history,
    schools,
    neighbourhoodDemographics,
    municipality,
    neighbourhood,
    appraisal,
    flags,
    shouldScramble,
    complianceRequirement,
    hideSquareFootage,
  } = data;

  const {
    onRoomsShowDetailsClick,
    onContactForPriceClick,
    onViewListingClick,
    onComplianceRequirementClick,
  } = handlers;

  const { t } = useTranslation(Namespaces.COMMON);
  const tenant = getTenantById(tenantId);
  const isNotMobile = useIsNotMobile();
  const localPriceHistoryRef = useRef<HTMLDivElement>(null);
  // Use external ref if provided (for sticky header button), otherwise use local ref
  const priceHistoryRef = externalPriceHistoryRef || localPriceHistoryRef;

  const listingCountryCode = address.countryCode.toUpperCase() === CountryCodeList.CANADA 
    ? CountryCodeList.CANADA 
    : CountryCodeList.UNITED_STATES;
  const { canViewSchools, canViewWalkScore, canViewPropertyAppraisal, canViewDemographics } = useListingRestrictions(listingCountryCode);
  const { marketStats } = listingComponentHeights;

  const hasAddressCoordinates = listing.position?.coordinates?.length === 2;
  const showStreetLevelLocation = listing.displayAddress !== false;
  const canShowMapOnTab = showStreetLevelLocation && hasAddressCoordinates;
  const isSchoolTabVisible = canViewSchools && (schools?.length ?? 0) > 0 && hasAddressCoordinates;
  const isDemographicsTabVisible = canViewDemographics && (neighbourhoodDemographics as { id?: unknown })?.id;
  const isCommuteTabVisible = showStreetLevelLocation && hasAddressCoordinates;

  const isMyListingAgentVisible = hasFeatureFlag(flags, FEATURE_FLAGS.MY_LINK_MY_LEAD_ENABLED) && tenant.features.MyLinkMyLead && !isNotMobile;
  const isPropertyAppraisalVisible = hasFeatureFlag(flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED) && canViewPropertyAppraisal && appraisal?.appraisalResult?.isAvailable;
  const isChartsSectionVisible = hasFeatureFlag(flags, FEATURE_FLAGS.CHARTS_ENABLED) && municipality;
  const isSecondaryInfoSectionVisible = hasFeatureFlag(flags, FEATURE_FLAGS.SECONDARY_INFO_ENABLED) && (isSchoolTabVisible || isDemographicsTabVisible || isCommuteTabVisible);
  const showMapTab = showStreetLevelLocation;
  // Redesigned single "Property Details" tab (Description + Key Facts + Additional Information from locale data)
  const showPropertyDetailsRedesign = !!tenant.features.ListingPropertyDetailsRedesign;

  // For Centris listings in the redesign, prefer the locale-specific rooms (localized
  // type/level) so the Rooms tab matches the active locale. All other cases keep the
  // top-level rooms unchanged.
  const roomsToDisplay = useMemo(
    () => resolveDisplayRooms(listing, showPropertyDetailsRedesign),
    [listing, showPropertyDetailsRedesign]
  );

  const chartContainerHeight = isNotMobile ? marketStats.desktop : marketStats.mobile;
  const fullAddress = address.address;

  const propertyType = t(getPropertyTypeLabelKey(listing.propertyType));
  const propertySubType = t(getPropertySubTypeLabelKey(listing.propertySubType));

  const keyFacts: ListingDetails = useMemo(() => ({
    mlsNum: listing.mlsNum,
    providerId: listing.providerId,
    isCondo: listing.isCondo,
    isRental: listing.isRental,
    isAvailable: listing.isAvailable,
    availableAt: listing.availableAt,
    approxAge: listing.misc?.approxAge,
    daysOnMarket: listing.daysOnMarket,
    lotSize: listing.lotSize,
    maintenanceFees: listing.maintenanceFees,
    taxes: listing.taxes,
    priceBySqft: listing.priceBySqft,
    type: propertySubType,
    levels: propertyType,
    sqftLabel: listing.sqftLabel,
    garage: listing.garage,
  }), [listing, propertyType, propertySubType]);

  const isInServiceTenant = isTenantExpInService(tenantId);
  const listingAgent = useMemo(() => ({
    // Retained for In-Service only: military badges exist on this legacy ListingAgent
    // design and are not incorporated in MyListingAgentCard.
    expAgentId: isInServiceTenant ? listing.expAgentId ?? undefined : undefined,
    showMilitaryDetails: isInServiceTenant,
  }), [listing.expAgentId, isInServiceTenant]);

  const locationUnavailableProps = tenant.theme.style_overrides?.locationUnavailable ?
    {
      src: tenant.theme.style_overrides.locationUnavailable.imagePath,
      message: tenant.theme.style_overrides.locationUnavailable.message,
    } :
    {};

  const isPrivate = complianceRequirement !== ComplianceRequirement.NONE;

  // Analytics tracking
  const trackKeyFactsView = useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally({ event: GTM_VIEW_ADDRESS_PAGE_KEY_FACTS_TAB }, isVisible);
  }, []);

  const trackPriceHistoryView = useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally({ event: GTM_VIEW_ADDRESS_PAGE_PRICE_HISTORY_TAB }, isVisible);
  }, []);

  const trackAvgSoldPriceView = useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally({ event: GTM_VIEW_ADDRESS_PAGE_AVERAGE_SOLD_PRICE_TAB }, isVisible);
  }, []);

  const trackZoopraisalView = useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally({ event: GTM_VIEW_ADDRESS_PAGE_ZOOPRAISAL }, isVisible);
  }, []);

  const onTabChanged = useCallback((tabId: string) => {
    if (tabId === TAB_IDS.DESCRIPTION) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_DESCRIPTION_TAB);
    } else if (tabId === TAB_IDS.ROOMS) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_ROOMS_TAB);
    } else if (tabId === TAB_IDS.MAP) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_MAP_TAB);
    } else if (tabId === TAB_IDS.SALES_ACTIVITY) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_SALES_ACTIVITY_TAB);
    } else if (tabId === TAB_IDS.DEMOGRAPHICS) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_DEMOGRAPHICS_TAB);
    } else if (tabId === TAB_IDS.COMMUTE) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_COMMUTE_TAB);
    } else if (tabId === TAB_IDS.SCHOOLS) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_SCHOOL_NAME);
    }
  }, []);

  const handleSchoolLinkClick = useCallback(() => {
    console.log('handleSchoolLinkClick');
  }, []);

  return (
    <div className={className}>
      <Box sx={{ px: { xs: 2, md: 0 }}}>
        {/* Main Tabs */}
        <Tabs testId={listingPageModalIds.listingKeyInfo} onTabChanged={onTabChanged}>
        {showPropertyDetailsRedesign && (
          <Tab id={TAB_IDS.PROPERTY_DETAILS} label={t(LOCALE_COMMON_PATHS.ADDRESS_PROPERTY_DETAILS_TAB_LABEL, { defaultValue: 'Property Details' })} testId={listingPageModalIds.listingKeyFactsTab} hideTitle>
            <InView onChange={trackKeyFactsView} triggerOnce>
              <PropertyDetails
                listing={listing}
                listingDetails={keyFacts}
                address={fullAddress}
                isPrivate={isPrivate}
                shouldScramble={shouldScramble}
                showWalkScore={canViewWalkScore && hasFeatureFlag(flags, FEATURE_FLAGS.WALK_SCORE_ENABLED)}
                localizedMaintenanceFeesLabel={t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_MAINTENANCE_FEES, { context: isTenantExpCA(tenantId) || isZoocasaTenant(tenantId) ? 'ca' : 'us' })}
                listingAgent={listingAgent}
                hideSquareFootage={hideSquareFootage}
                showPropertyAddress={showStreetLevelLocation}
              />
            </InView>
          </Tab>
        )}
        {!showPropertyDetailsRedesign && (
          <Tab id={TAB_IDS.KEY_FACTS} label={t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_TAB_LABEL, { defaultValue: 'Key Facts' })} testId={listingPageModalIds.listingKeyFactsTab}>
            <InView onChange={trackKeyFactsView} triggerOnce>
              <KeyFacts
                listingDetails={keyFacts}
                isPrivate={isPrivate}
                shouldScramble={shouldScramble}
                address={fullAddress}
                localizedMaintenanceFeesLabel={t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_MAINTENANCE_FEES, { context: isTenantExpCA(tenantId) || isZoocasaTenant(tenantId) ? 'ca' : 'us' })}
                listingAgent={listingAgent}
                hideSquareFootage={hideSquareFootage}
                showPropertyAddress={showStreetLevelLocation}
              />
            </InView>
          </Tab>
        )}
        {!showPropertyDetailsRedesign && (
          <Tab id={TAB_IDS.DESCRIPTION} label={t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_TAB_LABEL, { defaultValue: 'Description' })} testId={listingPageModalIds.listingDescriptionTab}>
            <Description
              listing={listing}
              isPrivate={isPrivate}
              shouldScramble={shouldScramble}
              showWalkScore={canViewWalkScore && hasFeatureFlag(flags, FEATURE_FLAGS.WALK_SCORE_ENABLED)}
            />
          </Tab>
        )}
        {roomsToDisplay && roomsToDisplay.length > 0 && (
          <Tab id={TAB_IDS.ROOMS} label={t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_TAB_LABEL, { defaultValue: 'Rooms' })} testId={listingPageModalIds.listingRoomsTab}>
            <Rooms
              rooms={roomsToDisplay}
              isPrivate={isPrivate}
              shouldScramble={shouldScramble}
              onShowDetailsClick={onRoomsShowDetailsClick}
              variant={showPropertyDetailsRedesign ? 'table' : 'grid'}
            />
          </Tab>
        )}
        {showMapTab && (
          <Tab id={TAB_IDS.MAP} label={t(LOCALE_COMMON_PATHS.ADDRESS_MAP_TAB_LABEL, { defaultValue: 'Map' })} isFullScreen={true} testId={listingPageModalIds.listingMapTab}>
            {canShowMapOnTab ? (
              <Map
                listingId={listing?.id + '-tab'}
                position={listing.position}
                address={fullAddress}
                className={styles.map}
                isInteractive={isNotMobile}
                loadWhenVisible
                trackEventLocation={TrackEventLocation.Page}
              />
            ) : (
              <LocationUnavailable {...locationUnavailableProps} />
            )}
          </Tab>
        )}
      </Tabs>

      {/* Price History */}
      {hasFeatureFlag(flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED) && !listing.hidePriceHistory ? (
        <InView onChange={trackPriceHistoryView} triggerOnce>
          <PriceHistory
            ref={priceHistoryRef}
            currentListingId={listing.id}
            isRental={listing.isRental}
            saleHistory={history.filter(h => !h.isRental)}
            rentalHistory={history.filter(h => h.isRental)}
            onComplianceRequirementClick={onComplianceRequirementClick}
            onContactForPriceClick={onContactForPriceClick}
            onViewListingClick={onViewListingClick}
          />
        </InView>
      ) : (
        <div ref={priceHistoryRef} className={styles['price-history-placeholder']} />
      )}

      {/* My Listing / My Link Agent Card (mobile: below price history) */}
      {isMyListingAgentVisible && <MyListingAgentSection listing={listing} />}

      {/* Provider Disclaimer */}
      {hasFeatureFlag(flags, FEATURE_FLAGS.PROVIDER_DISCLAIMER_ENABLED) && (
        <ProviderDisclaimer listingInfo={listing} tenantId={tenantId} />
      )}

      {/* Property Appraisal */}
      {isPropertyAppraisalVisible && (
        <InView onChange={trackZoopraisalView} triggerOnce>
          <PropertyAppraisal
            tenantName={tenant.theme.name}
            agentDisclaimerLabel={''}
            renderTitle={() => <><MortgageCtaBanner /><AppraisalTitle tenantId={tenantId} /></>}
            price={listing.price}
            daysOnMarket={listing.daysOnMarket}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            appraisal={appraisal.appraisalResult}
            sellingRange={appraisal?.sellingRange}
            hideAvm={listing.hideAvm}
            onContactAnAgentClick={appraisal?.onContactAnAgentClick}
          />
        </InView>
      )}

      {/* Charts */}
      {isChartsSectionVisible && (
        <div style={{ minHeight: chartContainerHeight }}>
          <Tabs testId={listingPageModalIds.charts} className={styles['smdc-tabs']} onTabChanged={onTabChanged}>
            <Tab id={TAB_IDS.AVG_SOLD_PRICE} label={t(LOCALE_COMMON_PATHS.ADDRESS_AVG_SOLD_PRICE_TAB_LABEL, { defaultValue: 'Avg. Sold Price' })} testId={listingPageModalIds.avgSoldPriceTab}>
              <InView onChange={trackAvgSoldPriceView} triggerOnce>
                <ChartGroupLoading
                  testId={listingPageModalIds.avgSoldPriceTab}
                  listing={listing}
                  isBlurred={isPrivate}
                  type="market-stats"
                  chartData={municipality}
                  neighbourhoodData={neighbourhood}
                />
              </InView>
            </Tab>
            <Tab id={TAB_IDS.SALES_ACTIVITY} label={t(LOCALE_COMMON_PATHS.ADDRESS_SALES_ACTIVITY_TAB_LABEL, { defaultValue: 'Sales Activity' })} testId={listingPageModalIds.salesActivityTab}>
              <ChartGroupLoading
                testId={listingPageModalIds.salesActivityTab}
                listing={listing}
                isBlurred={isPrivate}
                type="market-competition"
                chartData={municipality}
                neighbourhoodData={neighbourhood}
              />
            </Tab>
          </Tabs>
        </div>
      )}

      {/* Secondary Info (Schools, Demographics, Commute) */}
      {isSecondaryInfoSectionVisible && (
        <Tabs testId={listingPageModalIds.secondaryInfo} className={styles['smdc-tabs']} onTabChanged={onTabChanged}>
          {isSchoolTabVisible && (
            <Tab id={TAB_IDS.SCHOOLS} label={t(LOCALE_COMMON_PATHS.ADDRESS_SCHOOLS_TAB_LABEL, { defaultValue: 'Schools' })} testId={listingPageModalIds.listingSchoolsTab}>
              <NearbySchools
                position={listing.position}
                schools={schools}
                onSchoolLinkClick={handleSchoolLinkClick}
              />
            </Tab>
          )}
          {isDemographicsTabVisible && (
            <Tab id={TAB_IDS.DEMOGRAPHICS} label={t(LOCALE_COMMON_PATHS.ADDRESS_DEMOGRAPHICS_TAB_LABEL, { defaultValue: 'Demographics' })} testId={listingPageModalIds.listingDemographicsTab}>
              <NeighbourhoodDemographics neighbourhoodId={listing.neighbourhood?.id} neighbourhood={neighbourhoodDemographics} />
            </Tab>
          )}
          {isCommuteTabVisible && (
            <Tab id={TAB_IDS.COMMUTE} label={t(LOCALE_COMMON_PATHS.ADDRESS_COMMUTE_TAB_LABEL, { defaultValue: 'Commute' })} testId={listingPageModalIds.listingCommuteTab}>
              <TravelTime address={listing} />
            </Tab>
          )}
        </Tabs>
      )}
      </Box>
    </div>
  );
}

export default ContentSectionControl;
